import { TGetListCompanyDto } from "@/api/services/admin/company/dtos/get-company-list.dto";
import { IconChevronRight } from "@/assets/icons";
import { IconBox } from "@/components/elements/IconBox";
import { Linkable } from "@/components/elements/Linkable";
import { Table } from "@/components/elements/Table";
import { DATE_FORMAT, DATE_TIME_FORMAT } from "@/constants/datetime";
import { EMPTY_STR } from "@/constants/string";
import { CompanyTableSorting } from "@/features/company/components/CompanyTableSorting";
import { COMPANY_LIST_TAB } from "@/features/company/constants/companyListTab";
import { useTable } from "@/hooks/useTable";
import { AppRoutes } from "@/routes/config";
import { ECompanyContractStatus } from "@/types/enum";
import { dayjs } from "@/utils/dayjs";
import { formatNumber } from "@/utils/number";
import { truncateWithEllipsis } from "@/utils/string";
import { getDynamicRoute } from "@/utils/url";
import { Box } from "@mui/material";
import { MRT_ColumnDef } from "material-react-table";
import { FC, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";

type CompanyTableProps = {
  contractStatuses: ECompanyContractStatus[];
  data: TGetListCompanyDto[];
  isLoading: boolean;
};

export const CompanyTable: FC<CompanyTableProps> = ({ contractStatuses, data, isLoading }) => {
  const navigate = useNavigate();
  const columns = useMemo<MRT_ColumnDef<TGetListCompanyDto>[]>(
    () => [
      {
        header: "企業名",
        id: "companyName",
        accessorKey: "companyName",
        Cell: ({ row }) => truncateWithEllipsis(row.original.companyName, 20),
        Header: <CompanyTableSorting name="company.companyName">企業名</CompanyTableSorting>,
      },
      {
        header: "企業ID",
        id: "companyCode",
        accessorKey: "companyCode",
        size: 100,
      },
      {
        header: "課金人数(1日)",
        size: 120,
        Cell: ({ row }) => {
          return row.original.totalFirstDayEnabledBillings || 0;
        },
      },
      {
        header: "課金人数(現在)",
        Cell: ({ row }) => {
          return row.original.totalCurrentEnabledBillings || 0;
        },
        size: 40,
      },
      {
        header: "課金対象数",
        size: 120,
        Cell: ({ row }) => {
          return row.original.currentMonthMaxBillableEmployeeCount ?? EMPTY_STR.TEXT;
        },
      },
      {
        header: "前月課金数",
        size: 120,
        Cell: ({ row }) => {
          return row.original.previousMonthMaxBillableEmployeeCount ?? EMPTY_STR.TEXT;
        },
      },
      {
        header: "単価",
        Cell: ({ row }) => (row.original.pricePerEmployee ? `${formatNumber(row.original.pricePerEmployee)}円` : EMPTY_STR.TEXT),
        size: 20,
      },
      {
        header: "分野",
        Cell: ({ row }) => row.original?.specificIndustry?.companySpecificIndustry || EMPTY_STR.TEXT,
        Header: <CompanyTableSorting name="specificIndustry.companySpecificIndustry">分野</CompanyTableSorting>,
        size: 20,
      },
      {
        header: "申請数",
        id: "applicationCount",
        size: 60,
        Cell: ({ row }) => row.original.applicationCount ?? 0,
      },
      {
        header: "contractStatus",
        Header: () => {
          if (contractStatuses.includes(ECompanyContractStatus.DURING_CONTRACT))
            return <CompanyTableSorting name="company.contractStartDate">契約開始日</CompanyTableSorting>;
          if (contractStatuses.includes(ECompanyContractStatus.EXPIRED_CONTRACT))
            return <CompanyTableSorting name="company.contractEndDate">解約日</CompanyTableSorting>;
          if (contractStatuses.includes(ECompanyContractStatus.TRIAL))
            return <CompanyTableSorting name="company.trialStartDate">トライアル開始日</CompanyTableSorting>;
          if (contractStatuses.includes(ECompanyContractStatus.TRIAL_CANCELLED))
            return <CompanyTableSorting name="company.trialEndDate">トライアル終了日</CompanyTableSorting>;
        },
        Cell: ({ row }) => {
          if (contractStatuses.includes(ECompanyContractStatus.DURING_CONTRACT))
            return row.original.contractStartDate ? dayjs(row.original.contractStartDate).format(DATE_FORMAT) : EMPTY_STR.DATE;
          if (contractStatuses.includes(ECompanyContractStatus.EXPIRED_CONTRACT))
            return row.original.contractEndDate ? dayjs(row.original.contractEndDate).format(DATE_FORMAT) : EMPTY_STR.DATE;
          if (contractStatuses.includes(ECompanyContractStatus.TRIAL))
            return row.original.trialStartDate ? dayjs(row.original.trialStartDate).format(DATE_FORMAT) : EMPTY_STR.DATE;
          if (contractStatuses.includes(ECompanyContractStatus.TRIAL_CANCELLED))
            return row.original.trialEndDate ? dayjs(row.original.trialEndDate).format(DATE_FORMAT) : EMPTY_STR.DATE;
        },
        size: 120,
      },
      {
        header: "最終ログイン日時",
        id: "lastLoginAt",
        size: 150,
        Cell: ({ row }) => (row.original.lastLoginAt ? dayjs(row.original.lastLoginAt).format(DATE_TIME_FORMAT) : EMPTY_STR.DATE),
      },
      {
        header: "詳細",
        id: "action",
        Cell: ({ row }) => {
          const { id } = row.original;
          return (
            <Linkable href={getDynamicRoute(AppRoutes.companyDetail, { id })}>
              <IconBox>
                <IconChevronRight />
              </IconBox>
            </Linkable>
          );
        },
        size: 20,
      },
    ],
    [contractStatuses],
  );

  const { table } = useTable({
    columns,
    rows: data,
    onClickRow: (row) => navigate(getDynamicRoute(AppRoutes.companyDetail, { id: row.id })),
    pinningColumns: ["action"],
    muiTableBodyRowProps: ({ row }) => ({
      className: row.original.deletedAt ? "deleted" : "",
    }),
  });

  return (
    <StyledTable>
      <Table table={table} isLoading={isLoading} maxHeight="calc(100vh - 420px)" />
    </StyledTable>
  );
};

const StyledTable = styled(Box)`
  .deleted {
    opacity: 0.5;
  }
`;

// Sort fields shared by every tab (企業名, 分野).
export const COMPANY_COMMON_SORT_FIELDS = ["company.companyName", "specificIndustry.companySpecificIndustry"];

// Date sort field that only applies to its own tab (the date column shown changes per tab).
export const COMPANY_TAB_DATE_SORT_FIELD = {
  [COMPANY_LIST_TAB.DURING_CONTRACT]: "company.contractStartDate",
  [COMPANY_LIST_TAB.EXPIRED_CONTRACT]: "company.contractEndDate",
  [COMPANY_LIST_TAB.TRIAL]: "company.trialStartDate",
  [COMPANY_LIST_TAB.TRIAL_CANCELLED]: "company.trialEndDate",
};
